import { z } from 'zod';
import { translateChartToJSON } from '@sisense/sdk-ui/analytics-composer';
import { BuildChartContext, updateChartQuerySchema } from '../types';
import { generateChart } from '../create-chart/create-chart';
import { applyPriorChartStyle } from './apply-prior-chart-style';
import { devDebug } from '@/utils/dev-debug';

export const updateChartQuery = async (
	{ revisionPrompt, chartId }: z.infer<typeof updateChartQuerySchema>,
	context: BuildChartContext,
) => {
	devDebug('toolCall', {
		tool: 'updateChartQuery',
		revisionPrompt,
		chartId,
	});

	const priorChart = context.retrieveChart?.(chartId);
	if (!priorChart) throw new Error(`Chart not found: ${chartId}`);

	if (!priorChart.queryState) {
		throw new Error(
			`Cannot revise chart ${chartId} without prior queryState. The chart may not have been created via NLQ or the query state was not preserved.`,
		);
	}

	const { widgetProps } = await generateChart(
		{
			dataSourceTitle: context.dataSourceTitle,
			queryPrompt: revisionPrompt,
			chartPrompt: revisionPrompt,
			chartType: null,
			chartTitle: priorChart.title ?? '',
			queryId: null,
			priorQueryState: priorChart.queryState,
		},
		context,
	);

	applyPriorChartStyle(widgetProps, priorChart);

	const chartTranslation = translateChartToJSON(widgetProps);
	if (chartTranslation.success) {
		widgetProps.chartState = {
			...chartTranslation.data,
			...(widgetProps.title && { title: widgetProps.title }),
		};
	}

	context.saveChart?.(chartId, widgetProps);

	return {
		message: `Chart query revised successfully`,
		chartState: widgetProps.chartState,
		dataset: widgetProps.dataset,
	};
};
